import React, { useState, ReactElement, useMemo } from "react";

import { useParams, Link, useLocation } from "wouter";
import { Grid, Column } from "../../../../../components/src";
import { Alert } from "../../../../../components/src/Alert";
import { Dropdown } from "../../../../../components/src/Dropdown";

import { YesNoModal } from "../../../../../components/src/YesNoModal";
import { useMutation } from "urql";
import { DELETE_INSTRUMENT } from "../../../../../graphql/src/party/queries";
import { PartyPageData } from "../PartyPage";
import { PartyInstrumentForm } from "../PartyInstrumentForm";
import { CashOrBalanceInstrumentForm, instrumentTypes, NewCashOrBalanceInstrumentButton } from "../CashOrBalanceInstrumentForm";
import { InstrumentProductTypeEnum, PartyAccountType } from "../../../types.generated";
import { formikUrqlErrorFormater } from "../../../../../components/src/common/formik-urql-error-helper";

export const PartyInstrumentsTab = ({ data, refetch }: { data: PartyPageData; refetch: () => void }): ReactElement => {
    const { id: partyId, itemId } = useParams<{ id: string; tabId: string; itemId: string }>();
    const [, navigate] = useLocation();

    const [alert, setAlert] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: null });

    const [_, deleteInstrument] = useMutation(DELETE_INSTRUMENT);

    const { party: currentParty, selectedInstrument } = useMemo(() => {
        const party = data?.party;
        let selectedInstrument = null;
        if (party && party.instruments && itemId) {
            const instr = party.instruments.find((a) => a._id.toString() === itemId.toString());
            selectedInstrument = instr;
        }
        return { party, selectedInstrument };
    }, [data?.party, itemId]);

    const selectInstrument = (instrumentId): void => {
        navigate(`/parties/${partyId}/instruments/${instrumentId}`, { replace: true });
    };

    if (!currentParty) return <div></div>;

    return (
        <div className="mx-3">
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete party instrument with id " + modal.payload.instrumentId + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        deleteInstrument({ _id: modal.payload.instrumentId })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    const message = formikUrqlErrorFormater(result.error);
                                    setAlert({ color: "danger", visible: true, message });
                                } else {
                                    refetch();
                                    navigate(`/parties/${partyId}/instruments`);
                                }
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }}
                />
            ) : null}
            <div className="flex flex-wrap">
                These are instruments such as bank cash accounts that are specific to party.
                <br />
            </div>
            <div id="partyinstrumentsform" className="partyinstrumentsform form">
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2 mb-4">
                        <div className="mb-2" style={{ float: "right" }}>
                            <div className="mt-1">
                                <NewCashOrBalanceInstrumentButton page={"parties/" + partyId + "/instruments"} />
                            </div>
                        </div>
                    </div>
                </div>
                {alert ? (
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full px-2 mb-4">
                            <Alert style={{ marginTop: "10px" }} variant="danger" onClose={() => setAlert(null)} dismissible>
                                <p>{alert}</p>
                            </Alert>
                        </div>
                    </div>
                ) : null}
                <div className="flex flex-wrap -mx-2">
                    <div className={itemId ? "w-full lg:w-8/12 px-2 mb-4" : "w-full px-2 mb-4"}>
                        <Grid data={currentParty.instruments} sortable tableClassName="table-xs">
                            <Column
                                field="_id"
                                format={(field) => {
                                    return (
                                        <a
                                            href="#selectInstrument"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                selectInstrument(field);
                                            }}
                                        >
                                            {field}
                                        </a>
                                    );
                                }}
                            />
                            <Column
                                field="name"
                                className="grid-column-sticky"
                                format={(value, item) => {
                                    return item && item._id ? <Link to={"/instruments/" + item._id}>{value}</Link> : value;
                                }}
                            />
                            <Column field="description" />
                            <Column title="IBAN" field="iban" />
                            <Column
                                field="_id"
                                title=""
                                format={(value) => {
                                    return (
                                        <Dropdown>
                                            <Dropdown.Toggle variant="danger" size="sm">
                                                Delete
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    as="button"
                                                    onClick={async () => {
                                                        setModal({ showModal: true, payload: { instrumentId: value, partyId: partyId } });
                                                    }}
                                                >
                                                    Delete instrument
                                                </Dropdown.Item>
                                                <Dropdown.Item>Cancel</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    );
                                }}
                            />
                        </Grid>
                    </div>

                    {(itemId &&
                        selectedInstrument &&
                        (selectedInstrument.productType === InstrumentProductTypeEnum.CashAccount ||
                            selectedInstrument.productType === InstrumentProductTypeEnum.BalanceAccount)) ||
                    (itemId && !selectedInstrument) ||
                    (itemId && (itemId === "newcashaccount" || itemId === "newbalanceaccount")) ? (
                        <div className="w-full lg:w-4/12 px-2 mb-4">
                            <Link className="close-btn float-right" to={`/parties/${partyId}/instruments`} />
                            <CashOrBalanceInstrumentForm
                                id={itemId.startsWith("new") ? null : itemId}
                                cid={currentParty._id}
                                parties={data.parties ? (data.parties as Array<{ _id: string; name: string }>) : []}
                                productType={
                                    itemId.startsWith("new")
                                        ? instrumentTypes.find((d) => d.toLowerCase() === itemId.substring(3, itemId.length))
                                        : null
                                }
                                onCreate={() => {
                                    refetch();
                                }}
                                onUpdate={() => {
                                    refetch();
                                }}
                            />
                        </div>
                    ) : itemId &&
                      selectedInstrument &&
                      selectedInstrument.productType !== InstrumentProductTypeEnum.CashAccount &&
                      selectedInstrument.productType !== InstrumentProductTypeEnum.BalanceAccount ? (
                        <div className="w-full lg:w-4/12 px-2 mb-4">
                            <Link className="close-btn float-right" to={`/parties/${partyId}/instruments`} />
                            <PartyInstrumentForm
                                key={`itemId-${selectedInstrument?._id ? selectedInstrument._id : "new"}`}
                                partyId={currentParty._id}
                                instrument={selectedInstrument}
                                externalAccounts={currentParty.externalAccounts}
                                accounts={currentParty.accounts.filter((account) => account.type !== PartyAccountType.Sum)}
                                parties={data.parties ? (data.parties as Array<{ _id: string; name: string }>) : []}
                                onUpdate={() => {
                                    refetch();
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
